import { ErrorModal } from "@dexteel/mesf-core";
import CloseIcon from "@mui/icons-material/Close";
import {
	Alert,
	Box,
	Button,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	IconButton,
	Stack,
	Typography,
	useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { type ReactNode, useState } from "react";

import { useTestShackReportDrawingPreviewQuery } from "@/pages/products/test-shack/hooks/queries/useTestShackReportDrawingPreviewQuery";

interface TestShackReportImageDialogProps {
	sectionName: string;
	uploadFileId: number | null;
	label: string;
	onClose: () => void;
	children?: ReactNode;
}

const DIALOG_TITLE_ID = "test-shack-report-image-dialog-title";

export const TestShackReportImageDialog = ({
	sectionName,
	uploadFileId,
	label,
	onClose,
	children,
}: TestShackReportImageDialogProps) => {
	const theme = useTheme();
	const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));
	const [error, setError] = useState("");
	const [failedUrl, setFailedUrl] = useState<string | null>(null);
	const imageQuery = useTestShackReportDrawingPreviewQuery({
		uploadFileId,
		scope: "dialog",
		onError: (queryError) => setError(queryError.message),
	});
	const { objectUrl, isPdf, aspectRatio: pdfAspectRatio } = imageQuery;
	const previewFailed = objectUrl !== null && failedUrl === objectUrl;
	const handlePreviewFailure = () => {
		setFailedUrl(objectUrl);
		setError(`${label} could not be displayed.`);
	};

	return (
		<>
			<Dialog
				open
				onClose={onClose}
				fullScreen={fullScreen}
				fullWidth
				maxWidth="xl"
				aria-labelledby={DIALOG_TITLE_ID}
			>
				<DialogTitle id={DIALOG_TITLE_ID} sx={{ pr: 6 }}>
					<Typography component="span" fontWeight={800}>
						Historical reference · {sectionName}
					</Typography>
					<IconButton
						aria-label="Close image viewer"
						onClick={onClose}
						sx={{ position: "absolute", right: 12, top: 12 }}
					>
						<CloseIcon />
					</IconButton>
				</DialogTitle>
				<DialogContent dividers>
					<Stack spacing={1.5} sx={{ minHeight: "65vh" }}>
						{children}
						<Box
							sx={{
								alignItems: "center",
								bgcolor: "background.default",
								border: 1,
								borderColor: "divider",
								borderRadius: 1,
								display: "flex",
								flex: 1,
								justifyContent: "center",
								minHeight: 420,
								overflow: "hidden",
								p: 1,
							}}
						>
							{uploadFileId === null ? (
								<Alert severity="info">
									No reference images are available for this inspection.
								</Alert>
							) : imageQuery.isError || previewFailed ? (
								<Alert severity="error" role="alert">
									{label} could not be loaded.
								</Alert>
							) : imageQuery.isLoading || objectUrl === null ? (
								<Stack alignItems="center" spacing={1} role="status">
									<CircularProgress size={32} />
									<Typography>Loading {label.toLowerCase()}…</Typography>
								</Stack>
							) : isPdf ? (
								<Box
									component="iframe"
									src={`${objectUrl}#toolbar=0&view=Fit`}
									title={`${label} PDF preview`}
									onError={handlePreviewFailure}
									sx={
										pdfAspectRatio
											? {
													aspectRatio: pdfAspectRatio,
													border: 0,
													width: `min(100%, calc(65vh * ${pdfAspectRatio}))`,
												}
											: { border: 0, height: "65vh", width: "100%" }
									}
								/>
							) : (
								<Box
									component="img"
									src={objectUrl}
									alt={`${label} for ${sectionName}`}
									onError={handlePreviewFailure}
									sx={{
										maxHeight: "65vh",
										maxWidth: "100%",
										objectFit: "contain",
									}}
								/>
							)}
						</Box>
					</Stack>
				</DialogContent>
				<DialogActions>
					<Button onClick={onClose} color="inherit">
						Close
					</Button>
				</DialogActions>
			</Dialog>
			<ErrorModal error={error} onHide={() => setError("")} />
		</>
	);
};
